import { useSelector } from 'react-redux';
import i18n from 'i18next';

import { getLastExchange } from '../../../../Checkout/store/order/selectors';

import {
	EXCHANGE_ROUTES,
	getIsAllPassengersSelected,
	getIsAllSegmentsSelected,
	getIsLoadingExchange,
	getPassengersIds,
	getSelectedPassengers,
	getSelectedSegments,
	useSkipPassengers
} from '@entities/exchange';
import { useRouter } from '@shared/lib/router';
import { CdnIconNames } from '@shared/ui';
import { ExchangeRefundBreadcrumb } from '@entities/order';
import { useIsMobile } from '../../../../utils';

export function useExchangeBreadcrumbs() {
	const router = useRouter();
	const isMobile = useIsMobile();

	const lastExchange = useSelector(getLastExchange);
	const isLoading = useSelector(getIsLoadingExchange);

	const passengersIds = useSelector(getPassengersIds);
	const selectedPassengers = useSelector(getSelectedPassengers);
	const selectedSegments = useSelector(getSelectedSegments);
	const isAllPassengersSelected = useSelector(getIsAllPassengersSelected);
	const isAllSegmentsSelected = useSelector(getIsAllSegmentsSelected);

	const skipPassengers = useSkipPassengers();

	let onNavigateToPassengers: (() => void) | null =
		router.currentPath !== EXCHANGE_ROUTES.passengers
			? () => {
					router.navigate(EXCHANGE_ROUTES.passengers);
			  }
			: null;

	let passengersIcon: CdnIconNames | undefined = isMobile ? 'Info/Passengers' : 'Action/Close-Clear';

	if (skipPassengers) {
		onNavigateToPassengers = null;
		passengersIcon = undefined;
	}

	const onNavigateToSegments =
		router.currentPath !== EXCHANGE_ROUTES.segments
			? () => {
					router.navigate(EXCHANGE_ROUTES.segments);
			  }
			: null;

	const breadcrumbs: ExchangeRefundBreadcrumb[] = [];

	if (!lastExchange) {
		if (router.currentPath !== EXCHANGE_ROUTES.passengers) {
			if (isAllPassengersSelected && passengersIds.length > 1) {
				breadcrumbs.push({
					id: 'passengers/all',
					label: i18n.t('Segments.AllPassengers', { ns: 'Exchange' }),
					passengersCount: passengersIds.length,
					icon: passengersIcon,
					disabled: isLoading,
					onAction: onNavigateToPassengers
				});
			} else {
				selectedPassengers.forEach(passenger =>
					breadcrumbs.push({
						id: `passenger/${passenger.id}`,
						label: passenger.values.fullName,
						icon: passengersIcon,
						disabled: isLoading,
						onAction: onNavigateToPassengers
					})
				);
			}
		}

		if (router.currentPath !== EXCHANGE_ROUTES.segments) {
			if (isAllSegmentsSelected) {
				breadcrumbs.push({
					id: 'segments/all',
					label: i18n.t('Segments.AllSegments', { ns: 'Exchange' }),
					icon: isMobile ? 'Services/Refundable' : 'Action/Close-Clear',
					disabled: isLoading,
					onAction: onNavigateToSegments
				});
			} else {
				selectedSegments.forEach(segment =>
					breadcrumbs.push({
						id: `segments/${segment.id}`,
						label: segment.label,
						icon: isMobile ? 'Services/Refundable' : 'Action/Close-Clear',
						disabled: isLoading,
						onAction: onNavigateToSegments
					})
				);
			}
		}
	}

	return breadcrumbs;
}
